import React, { useState, useRef } from 'react';
import { useMeetingNotes, useCreateMeetingNote, useUpdateMeetingNote, useDeleteMeetingNote } from '../hooks/useMeetingNotes.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import Modal from '../components/Modal.tsx';
import { toast } from 'react-hot-toast';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Calendar, FileText, Edit2, Trash2, Plus, Eye } from 'lucide-react';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const MeetingNotesPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', content: '' });
  const editorRef = useRef(null);
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();
  const { data, isLoading, error } = useMeetingNotes();
  const createNote = useCreateMeetingNote();
  const updateNote = useUpdateMeetingNote();
  const deleteNote = useDeleteMeetingNote();

  const notes = data?.data || [];

  // Rich text editor functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleEditorChange = () => {
    if (editorRef.current) {
      setForm(f => ({ ...f, content: editorRef.current.innerHTML }));
    }
  };

  const openAdd = () => {
    setForm({ title: '', date: new Date().toISOString().slice(0, 10), content: '' });
    setEditNote(null);
    setShowModal(true);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
    }, 100);
  };
  const openEdit = (note) => {
    setForm({ title: note.title, date: note.date, content: note.content });
    setEditNote(note);
    setShowModal(true);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = note.content || '';
      }
    }, 100);
  };
  const openView = (note) => {
    setViewNote(note);
  };
  const closeModal = () => {
    setShowModal(false);
    setEditNote(null);
  };
  const closeView = () => setViewNote(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editNote) {
        await updateNote.mutateAsync({ id: editNote.id, data: form });
        toast.success('Note updated');
      } else {
        await createNote.mutateAsync(form);
        toast.success('Note created');
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save note');
    }
  };
  const handleDelete = async (note) => {
    if (window.confirm('Delete this note?')) {
      try {
        await deleteNote.mutateAsync(note.id);
        toast.success('Note deleted');
      } catch (err) {
        toast.error('Failed to delete note');
      }
    }
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_MEETINGS}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-3 sm:p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
                <FileText className="text-blue-600" size={28} />
                Meeting Notes
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                Document and track important meeting discussions
              </p>
            </div>
            {isAdmin && (
              <PermissionGate permission={PERMISSIONS.CREATE_MEETING_NOTE}>
                <button
                  onClick={openAdd}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
                >
                  <Plus size={20} />
                  Add Note
                </button>
              </PermissionGate>
            )}
          </div>

          {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading notes...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="text-red-500 text-lg">Error loading notes</div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-20">
            <FileText size={64} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No meeting notes yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">Start documenting your meetings by creating your first note</p>
            {isAdmin && can(PERMISSIONS.CREATE_MEETING_NOTE) && (
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
              >
                <Plus size={20} />
                Create First Note
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {notes.map(note => (
              <div 
                key={note.id} 
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 group"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 p-3 sm:p-4 border-b border-gray-200 dark:border-gray-600">
                  <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {note.title}
                  </h3>
                  <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                    <Calendar size={14} className="mr-1.5 flex-shrink-0" />
                    <span className="truncate">
                      {new Date(note.date).toLocaleDateString('en-US', { 
                        weekday: 'short',
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>

                {/* Card Content */}
                <div 
                  className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => openView(note)}
                >
                  <div className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm line-clamp-4 min-h-[60px] sm:min-h-[80px]">
                    <div 
                      className="prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{ 
                        __html: note.content && typeof note.content === 'string' 
                          ? note.content.slice(0, 200) + (note.content.length > 200 ? '...' : '')
                          : '<p class="text-gray-400 italic">No content available</p>'
                      }}
                    />
                  </div>
                </div>

                {/* Card Actions */}
                <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
                  <button
                    onClick={() => openView(note)}
                    className="flex items-center gap-1.5 text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    <Eye size={14} />
                    View
                  </button>
                  
                  {isAdmin && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      {can(PERMISSIONS.EDIT_MEETING_NOTE) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(note);
                          }}
                          className="p-1.5 sm:p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                      {can(PERMISSIONS.DELETE_MEETING_NOTE) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(note);
                          }}
                          className="p-1.5 sm:p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add/Edit Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={closeModal}>
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold mb-4">{editNote ? 'Edit Note' : 'Add Note'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes *</label>
                
                {/* Toolbar */}
                <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border border-gray-300 rounded-t-lg">
                  <button
                    type="button"
                    onClick={() => execCommand('bold')}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded"
                    title="Bold"
                  >
                    <Bold size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('italic')}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded"
                    title="Italic"
                  >
                    <Italic size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('underline')}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded"
                    title="Underline"
                  >
                    <Underline size={16} />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => execCommand('insertUnorderedList')}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded"
                    title="Bullet List"
                  >
                    <List size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('insertOrderedList')}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded"
                    title="Numbered List"
                  >
                    <ListOrdered size={16} />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => {
                      const url = prompt('Enter URL:');
                      if (url) execCommand('createLink', url);
                    }}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded"
                    title="Insert Link"
                  >
                    <LinkIcon size={16} />
                  </button>
                  <select
                    onChange={(e) => execCommand('formatBlock', e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm"
                    defaultValue=""
                  >
                    <option value="">Normal</option>
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="h3">Heading 3</option>
                  </select>
                </div>
                
                {/* Editor */}
                <div
                  ref={editorRef}
                  contentEditable
                  onInput={handleEditorChange}
                  className="w-full min-h-[200px] sm:min-h-[300px] px-3 sm:px-4 py-2 sm:py-3 border border-t-0 border-gray-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm sm:text-base"
                  style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    lineHeight: '1.6'
                  }}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Use the toolbar above to format your notes
                </p>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >Cancel</button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >{editNote ? 'Save Changes' : 'Add Note'}</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      {/* View Modal */}
      {viewNote && (
        <Modal isOpen={!!viewNote} onClose={closeView}>
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold mb-2">{viewNote.title}</h2>
            <div className="text-gray-500 text-xs sm:text-sm mb-4">{new Date(viewNote.date).toLocaleDateString()}</div>
            <div className="text-gray-800 mb-4 text-sm sm:text-base">
              <div 
                className="prose prose-sm sm:prose max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: viewNote.content && typeof viewNote.content === 'string' 
                    ? viewNote.content 
                    : 'No content available'
                }}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeView}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >Close</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
    </PermissionPage>
  );
};

export default MeetingNotesPage; 